import {
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useCallback, useEffect, useState } from 'react';

import { Stack } from '@atlaskit/primitives/stack';

import type { EligibleMessage as BackendMessageBody } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { SerializableRecord } from '@post-office/serializable-record';

import { usePlacementApi } from '@post-office/use-placement-api';

export type PlacementRenderProps = {
	/** Called by message template to dismiss itself from the UI */
	dismiss: () => void;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

export type PlacementFrontendProps = {
	preloadedInitialData?: DefaultMessagesResponse | undefined;
	onMessageCountChange?: (numberOfMessages: number) => void;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: 'atlas-home-for-you-main-content',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
		hideInIsolatedCloud: true,
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>(
	(Messages) =>
		({ onMessageCountChange, preloadedInitialData }) => {
			const [{ messages, status }, setMessagesState] = useState<{
				messages: BackendMessageBody<SerializableRecord>[] | undefined;
				status: 'loading' | 'done';
			}>(() => {
				if (preloadedInitialData) {
					return preloadedInitialData?.messages?.length > 0
						? {
								status: 'done',
								messages: preloadedInitialData?.messages,
							}
						: { messages: undefined, status: 'done' };
				}

				return { messages: undefined, status: 'loading' };
			});

			const isDone = status === 'done';

			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				autoRefetch: {
					isEnabled: !isDone,
					updateKey: 'atlas-home-for-you-main-content',
				},
			});

			useEffect(() => {
				if (typeof messages === 'undefined') {
					return;
				}

				if (!onMessageCountChange) {
					return;
				}

				onMessageCountChange(messages.length);
			}, [messages, onMessageCountChange]);

			const handleDismiss = useCallback(
				(messageInstanceId: string) => {
					setMessagesState(() => {
						if (!messages) {
							return { messages: undefined, status: 'done' };
						}

						const filteredMessages = messages.filter(
							(message) => messageInstanceId !== message.messageInstanceId,
						);

						return {
							messages: filteredMessages,
							status: 'done',
						};
					});
				},
				[messages],
			);

			useEffect(() => {
				if (!isLoading && !isDone) {
					setMessagesState({
						messages: data?.messages,
						status: 'done',
					});
				}
			}, [isLoading, isDone, data]);

			if (error) {
				return null;
			}

			if (status === 'loading') {
				return null;
			}

			if (!messages || messages.length === 0) {
				return null;
			}

			return (
				<Stack space="space.100">
					{messages.map((message, index) => (
						<Messages.render
							key={`${message.messageInstanceId}-${index}`}
							dismiss={() => handleDismiss(message.messageInstanceId)}
							{...message}
						/>
					))}
				</Stack>
			);
		},
);
